/**
 * List Item Renderer
 * 
 * Unified rendering for list items with checkboxes, priority indicators, etc.
 */

import { getPriorityIndicatorColor, type Priority } from './priority-colors.js';
import { designTokens } from './design-system.js';

export interface ListItemConfig {
  id: string;
  title: string;
  description?: string;
  completed?: boolean;
  priority?: Priority;
  metadata?: Record<string, unknown>;
  onClick?: () => void;
  onToggle?: () => void;
  onDelete?: () => void;
  showCheckbox?: boolean;
  showPriority?: boolean;
  showDelete?: boolean;
}

/**
 * Render a single list item
 */
export function renderListItem(config: ListItemConfig): string {
  const {
    id,
    title,
    description,
    completed = false,
    priority,
    onClick,
    onToggle,
    onDelete,
    showCheckbox = false,
    showPriority = !!priority,
    showDelete = !!onDelete
  } = config;
  
  // Set up click handlers
  if (onToggle) {
    (window as any)[`__listItemToggle_${id}`] = () => onToggle();
  }
  if (onDelete) {
    (window as any)[`__listItemDelete_${id}`] = () => onDelete();
  }
  if (onClick) {
    (window as any)[`__listItemClick_${id}`] = () => onClick();
  }
  
  const clickHandler = onClick ? `onclick="window.__listItemClick_${id} && window.__listItemClick_${id}();"` : '';
  const toggleHandler = onToggle ? `onchange="window.__listItemToggle_${id} && window.__listItemToggle_${id}();"` : '';
  const deleteHandler = onDelete ? `onclick="event.stopPropagation(); window.__listItemDelete_${id} && window.__listItemDelete_${id}();"` : '';
  
  return `
    <li 
      class="list-item" 
      data-item-id="${escapeHtml(id)}"
      style="
        display: flex;
        align-items: flex-start;
        gap: ${designTokens.padding.sm};
        opacity: ${completed ? '0.6' : '1'};
        list-style: none;
        cursor: ${onClick ? 'pointer' : 'default'};
      "
      ${clickHandler}
    >
      ${showCheckbox ? `
        <input 
          type="checkbox" 
          ${completed ? 'checked' : ''} 
          ${onToggle ? '' : 'disabled'}
          style="
            margin-top: 2px;
            cursor: ${onToggle ? 'pointer' : 'not-allowed'};
            accent-color: ${designTokens.contentColor};
          "
          ${toggleHandler}
        />
      ` : ''}
      <div style="
        flex: 1;
        font-size: ${designTokens.fontSize.sm};
        color: ${designTokens.contentColor};
        text-decoration: ${completed ? 'line-through' : 'none'};
        min-height: 20px;
      ">
        ${escapeHtml(title)}
        ${description ? `
          <div style="
            font-size: ${designTokens.fontSize.xs};
            color: ${designTokens.contentColorMuted};
            margin-top: 2px;
          ">
            ${escapeHtml(description)}
          </div>
        ` : ''}
      </div>
      ${showPriority && priority ? `
        <div style="
          width: ${designTokens.spacing.priorityIndicatorSize};
          height: ${designTokens.spacing.priorityIndicatorSize};
          background: ${getPriorityIndicatorColor(priority)};
          border-radius: 2px;
          flex-shrink: 0;
          margin-top: 4px;
        " title="Priority: ${priority}"></div>
      ` : ''}
      ${showDelete ? `
        <button 
          class="list-item-delete"
          data-item-id="${escapeHtml(id)}"
          style="
            background: transparent;
            border: none;
            color: ${designTokens.contentColorMuted};
            cursor: pointer;
            padding: ${designTokens.padding.xs};
            font-size: ${designTokens.fontSize.xs};
            opacity: 0.5;
            transition: opacity 0.2s;
          "
          title="Delete"
          ${deleteHandler}
        >×</button>
      ` : ''}
    </li>
  `;
}

/**
 * Render multiple list items
 */
export function renderListItems(items: ListItemConfig[]): string {
  return items.map(item => renderListItem(item)).join('');
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

